import {
  EmissionsConsumptionItem,
  SpecItem,
} from '@tribal-worldwide-london/fa-configuration-service-vwg';
import { StockSpecItem, StockSpecOption } from '@tribal-worldwide-london/fa-stock-service-vwg';
import {
  Option,
  PdbLong,
} from '@tribal-worldwide-london/fa-pdb-service-vwg';
import ContentManager from '@tribal-worldwide-london/fa-content';
import { Department, OpeningHours } from '@tribal-worldwide-london/fa-retailer-service-vwg';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import { ERROR_PDB_STATUS_KEYS } from '@/constants/error';
import { StockImageRels, CAPI_PREVIEW_IMAGE_RELS, type CapiImageRels } from '@/constants/images';
import { FeatureStatus } from '@/hooks/useFeatures';
import {
  GlobalStoreState,
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { CapiImage } from '../components';
import emissionsConsumptionMapping, {
  EmissionsConsumptionPhaseValues,
  EmissionsConsumptionValueTypes,
} from '../constants/emissionsConsumptionMapping';
import { engineType, fuelType, normalizeFuelTypeName } from '../constants/fuelType';
import { orderByKey, removeDuplicatesByKey } from './object';
import { SpecValuesByTitle } from '@/types';
import { notEmpty } from './array';
import { SpecOption, TechnologyItem } from '@tribal-worldwide-london/fa-product-service-vwg';

export type CarOptionsEndpoint = 'pdb' | 'config' | 'stock';

export type BespokeSpecItem = {
  category: string;
  description?: string;
  liked: boolean;
  optionId: string;
  price: number;
  selected: boolean;
  title: string;
  excludedFromFinance?: boolean;
};

export const getActiveConfiguration = (app: GlobalStoreState['app']) => {
  if (app.activeConfiguration !== undefined) {
    return app.configurations.filter(
      (_, index: number) => index === app.activeConfiguration
    )[0];
  }

  return;
};

export const getConfigImage = (
  configuration: GlobalStoreStateConfiguration,
  key: CapiImageRels | StockImageRels,
): CapiImage => {
  return (
    configuration.pdbData?.capi?.images.filter(
      (image: CapiImage) => image.title === key
    )[0] || { uri: '' }
  );
};

/** Side-profile preview, then the next preferred angle, then the first image. */
export const getPreviewImage = (
  images?: CapiImage[] | null
): CapiImage | undefined => {
  if (!images?.length) return undefined;

  for (const rel of CAPI_PREVIEW_IMAGE_RELS) {
    const match = images.find((image) => image.rel === rel);
    if (match) return match;
  }

  return images[0];
};

export const isInteriorOption = (
  configuration: GlobalStoreStateConfiguration,
  selectedOption: Option | SpecItem | StockSpecItem
) => {
  const id =
    ('id' in selectedOption && selectedOption.id) ||
    ('pr' in selectedOption && selectedOption.pr);

  return id && configuration.pdbData?.interior?.upholstery === id;
};

export const getImageOptions = (
  code: string,
  configuration: GlobalStoreStateConfiguration,
  selectedOption: Option | SpecItem | StockSpecItem
) => {
  if (!configuration.pdbData) return;

  const imageOptions = {
    derivativeId: configuration.pdbData.derivative?.id,
    modelId: configuration.pdbData.derivative?.modelId,
    optionId: isInteriorOption(configuration, selectedOption)
      ? configuration.pdbData?.interior?.['upholstery-colour']
      : code,
    isStockCar: isStockCar(configuration),
  };

  return imageOptions;
};

export const getCarOptionsData = (
  configuration: GlobalStoreStateConfiguration,
  code: string,
  endpoint: CarOptionsEndpoint = 'pdb'
) => {
  let selectedOption;

  if (isNotBuildable(configuration?.pdbData)) {
    return {
      availability: FeatureStatus.NOT_AVAILABLE,
      title: '',
      visible: false,
    };
  }

  switch (endpoint) {
    case 'pdb':
      selectedOption = configuration.pdbData?.options?.filter(
        (option: Option) => option.id === code
      )[0];

      break;
    case 'config':
      selectedOption = configuration.configData?.specItems.filter(
        (option: SpecItem) => option.pr === code
      )[0];

      break;
    case 'stock':
      selectedOption = configuration.stockData?.specItems.filter(
        (option: StockSpecItem) => option.pr === code
      )[0];
  }

  if (selectedOption) {
    return {
      ...selectedOption,
      title: isInteriorOption(configuration, selectedOption)
        ? configuration.pdbData?.interior?.title
        : selectedOption.title,
      imageOptions: getImageOptions(code, configuration, selectedOption),
    };
  } else {
    return null;
  }
};

export const getEmissionsConsumptionValue = (
  configuration: GlobalStoreStateConfiguration,
  fuelTypeName: string | null | undefined,
  type: EmissionsConsumptionValueTypes,
  phase: EmissionsConsumptionPhaseValues
) => {
  const resolvedFuelTypeName = normalizeFuelTypeName(fuelTypeName);
  if (!resolvedFuelTypeName) return;

  const valuesByFuel = emissionsConsumptionMapping[type]?.[resolvedFuelTypeName];
  if (!valuesByFuel) return;
  let valuesToMatch = valuesByFuel[phase];
  if (!valuesToMatch) return;

  const specData = configuration.stockData
    ? configuration.stockData
    : configuration.configData;

  // VIN PDBs leave engine.engineType null; the emissions payload still carries it.
  const resolvedEngineType =
    configuration.pdbData?.technicalSpecifications?.engine?.engineType ??
    specData?.emissionsConsumptionData?.engineType;

  if (resolvedEngineType === engineType.NOVC_HEV) {
    valuesToMatch = {
      ...valuesToMatch,
      energyManagementType: 'SUSTAINING',
    };
  }

  const availableKeys: Array<string> = [
    'attributeType',
    'valueType',
    'phase',
    'energyManagementType',
  ];

  if (valuesToMatch.fuelType) availableKeys.push('fuelType');

  return specData?.emissionsConsumptionData?.emissionsConsumption?.filter(
    (item: EmissionsConsumptionItem) =>
      availableKeys.every(
        (key) =>
          valuesToMatch[key] === item[key as keyof EmissionsConsumptionItem]
      )
  )[0];
};

//Get list of unique category 1 properties across all configurations and spec items
export const getCategory1List = (
  configurations: GlobalStoreStateConfiguration[]
) => {
  let category1Keys: string[] = [];

  configurations.forEach((configuration: GlobalStoreStateConfiguration) => {
    const specItems =
      configuration.configData?.specItems || configuration.stockData?.specItems;

    if (!!specItems) {
      category1Keys = category1Keys
        .concat(
          specItems.map(
            ({ category1 }: SpecItem | StockSpecItem) => category1 || ''
          )
        )
        .filter((category1Key) => !!category1Key);
    }
  });

  return category1Keys
    .filter(
      (value: string, i: number, currentValues: string[]) =>
        currentValues.indexOf(value) === i
    )
    .sort();
};

export const getReplacedSpecData = (
  replacedStandardSpecId: string,
  specOptions: any
) => {
  const replacedSpecData = specOptions?.filter(
    (item: any) => item.id === replacedStandardSpecId
  );
  return {
    ...replacedSpecData?.[0],
    availability: FeatureStatus.REPLACED
  };
};

// Get list of spec items grouped by title
export const getCategory1AndBespokeSpecItems = (
  configurations: GlobalStoreStateConfiguration[],
  contentManager: ContentManager,
) => {
  const categoryList = getCategory1List(configurations);
  // add category for all bespoke spec items at the end of array
  categoryList.push(contentManager.get('bespoke-spec-items-accordion-title'));

  const categorisedSpecItemData: Record<
    string,
    Record<string, SpecItem[] | StockSpecItem[]>
  > = {};
  const notIncluded = { availability: FeatureStatus.NOT_INCLUDED };
  const notAvailable = { availability: FeatureStatus.NOT_AVAILABLE };

  const addItem = (grouping: string, title: string) => {
    if (!categorisedSpecItemData[grouping][title]) {
      categorisedSpecItemData[grouping][title] = new Array(
        configurations.length
      ).fill(notIncluded);
    }
  };

  categoryList.forEach((grouping: string) => {
    categorisedSpecItemData[grouping] = {};

    configurations.forEach(
      (configuration: GlobalStoreStateConfiguration, i: number) => {
        const specItems =
          configuration.configData?.specItems ||
          configuration.stockData?.specItems;
        const bespokeSpecItems = configuration.bespokeSpecItems;
        const replacedStandardSpecs =
          configuration.configData?.replacedStandardSpecs ||
          configuration.stockData?.replacedStandardSpecs;
        const specOptions =
          configuration.marketingShortCode 
            ? configuration?.productData?.options 
            : configuration?.stockData?.options;

        

        (specItems || []).forEach((specItem: SpecItem | StockSpecItem) => {
          const { title, category1, visible } = specItem;
          if (category1 === grouping && visible) {
            addItem(grouping, title);
            categorisedSpecItemData[grouping][title][i] = specItem;
          }
        });

        (bespokeSpecItems || []).forEach((bespokeSpecItem: BespokeSpecItem) => {
          const bespokeSpecItemData = getCarOptionsData(configuration, bespokeSpecItem.optionId, 'pdb');
          const { title } = bespokeSpecItemData;
          if (contentManager.get('bespoke-spec-items-accordion-title') === grouping) {
            addItem(grouping, title);
            categorisedSpecItemData[grouping][title][i] = bespokeSpecItemData;
          }
        });

        (replacedStandardSpecs || []).forEach((replacedStandardSpecId: string) => {
          const replacedSpecData = getReplacedSpecData(replacedStandardSpecId, specOptions);
          const { category1, title, visible } = replacedSpecData;
          if (category1 === grouping && visible) {
            addItem(grouping, title);
            categorisedSpecItemData[grouping][title][i] = replacedSpecData;
          }
        });

        categorisedSpecItemData[grouping] = orderByKey(
          categorisedSpecItemData[grouping]
        );
      }
    );

    configurations.forEach(
      (configuration: GlobalStoreStateConfiguration, i: number) => {
        if (isNotBuildable(configuration.pdbData)) {
          Object.keys(categorisedSpecItemData[grouping]).forEach(
            (title: string) => {
              categorisedSpecItemData[grouping][title][i] =
                notAvailable as SpecItem;
            }
          );
        }
      }
    );
  });

  return categorisedSpecItemData;
};

export const isAllNotBuildable = (
  configurations: GlobalStoreStateConfiguration[]
) => {
  return configurations?.every((config) => isNotBuildable(config.pdbData));
};

export const isNotBuildable = (pdbItem: PdbLong | undefined) => {
  if (
    pdbItem === undefined ||
    typeof pdbItem.status === "string" ||
    pdbItem.status?.overall?.errorCode === "INTERNAL_SERVER_ERROR"
  )
    return true;

  return (
    ERROR_PDB_STATUS_KEYS.includes(pdbItem.status?.overall?.errorCode || '') ||
    ERROR_PDB_STATUS_KEYS.includes(pdbItem.status?.options?.errorCode || '')
  );
};

export const getBuildableConfigs = (
  configurations: GlobalStoreStateConfiguration[]
) => {
  return configurations.filter(({ pdbData }) => !isNotBuildable(pdbData));
};

export const getBuildableConfigIndices = (
  configurations: GlobalStoreStateConfiguration[]
) => {
  return configurations.map(({ pdbData }) => !isNotBuildable(pdbData));
};

export const getFirstBuildableConfiguration = (
  configurations: GlobalStoreStateConfiguration[]
) => {
  return configurations.filter(({ pdbData }) => !isNotBuildable(pdbData))[0];
};

export const stripPdbFromUrl = (url: string) => {
  if (!url.includes('/pdb')) return url;
  return url.replace('/pdb', '');
};

export const cubicCapacityToLitres = (
  cubicCapacity: number | null | undefined
) => {
  if (!cubicCapacity || cubicCapacity === -1) return;
  return (cubicCapacity / 1000).toFixed(1);
};

export const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('en-GB');
};

export const getCarConfigurationCode = (
  configuration: GlobalStoreStateConfiguration
) =>
  isStockCar(configuration)
    ? configuration.orderNumber ?? configuration.vin
    : configuration.marketingShortCode;

export const isStockCar = (configuration: GlobalStoreStateConfiguration) =>
  configuration.pdbData?.status.stockVehicleAvailable?.valid;

export const getNewCarsDepartment = (departments: Department[]) => {
  return departments.find(
    (department) => department.departmentType.name === 'New'
  );
};

export const getRetailerAddress = ([street, district, town, postcode]: Array<
  string | undefined
>) => {
  return [street, district, town, postcode].filter(Boolean).join(', ');
};

export const getRetailerOpeningTimes = (openingHours: OpeningHours | undefined) => {
  if (!openingHours) return;
  
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return weekdays.map((day) => {

    const openingTimeString = openingHours[
      `${day.toLowerCase()}Open` as keyof typeof openingHours
    ] as string;
    const closingTimeString = openingHours[
      `${day.toLowerCase()}Close` as keyof typeof openingHours
    ] as string;

    if (openingTimeString === 'closed' || closingTimeString === 'closed') {
      return { day, times: 'Closed' };
    }

    const openingTime = get12hrTimeString(openingTimeString);
    const closingTime = get12hrTimeString(closingTimeString);

    return { day, times: `${openingTime} - ${closingTime}` };
  });
};

export const get12hrTimeString = (timeString: string) =>
  new Date('1970-01-01T' + timeString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
  });

export const getCategorySpecs = (
  specs: SpecItem[] | StockSpecItem[],
  isStock = false
) => {
  const categories = specs
    .map((spec: SpecItem | StockSpecItem) => spec.category1)
    .filter(notEmpty);

  const getSpecs = (category: string) => {
    const features = specs
      .filter(
        (spec: SpecItem | StockSpecItem) =>
          spec.category1 === category &&
          spec.visible &&
          (spec.availability === FeatureStatus.STANDARD_SPEC ||
            spec.availability === FeatureStatus.REPLACED ||
            // On a VIN, optional-extra means the option is fitted. Negative
            // prices are grants/discounts (e.g. Electric Car Grant), not kit.
            (isStock &&
              spec.availability === FeatureStatus.OPTIONAL_EXTRA &&
              !(spec.price < 0)))
      )
      .sort((a: SpecItem | StockSpecItem, b: SpecItem | StockSpecItem) => {
        if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
        else if (a.title.toLowerCase() > b.title.toLowerCase()) return 1;
        return 0;
      });

    return features.length ? { [category]: features } : {};
  };

  const categoryWithSpecs = Array.from(new Set(categories))
    .sort()
    .reduce((acc, category) => {
      return {
        ...acc,
        ...getSpecs(category),
      };
    }, {});

  return categoryWithSpecs;
};

export const isWheelsFeature = (feature: SpecItem | SpecOption | StockSpecOption) => {
  return (
    feature.category1 === 'Appearance & Styling' &&
    feature.category2 === 'Wheels & Suspension' &&
    feature.visible &&
    feature.group === 'Wheels'
  );
};

export const getSlidesPerPage = (
  length: number,
  b960SlidesCount: number = 2,
  defaultSlidesCount: number = 1,
  b1280SlidesCount?: number,
  b1600SlidesCount?: number,
  b1920SlidesCount?: number
) => {
  const slidesPerPage: any = {
    [Breakpoints.default]: length === 1 ? 1 : defaultSlidesCount,
    [Breakpoints.b960]: b960SlidesCount,
  };

  if (b1280SlidesCount) {
    slidesPerPage[Breakpoints.b1280] = b1280SlidesCount;
  }

  if (b1600SlidesCount) {
    slidesPerPage[Breakpoints.b1600] = b1600SlidesCount;
  }

  if (b1920SlidesCount) {
    slidesPerPage[Breakpoints.b1920] = b1920SlidesCount;
  }

  return {
    slidesPerPage,
    hideNavigation: length <= Math.floor(b960SlidesCount),
  };
};

export const getCTACardSlidesPerPage = (length: number) => {
  return getSlidesPerPage(length, 2);
};

export const specIsInvalid = (spec: null | undefined | number | string) => {
  return spec === undefined || spec === null || spec === -1;
};

export const formatSpec = (
  spec: null | undefined | number | string,
  fallback: string,
  suffix?: string,
  toOneDecimalPlace?: boolean
) => {
  if (specIsInvalid(spec)) return fallback;

  const specValue =
    typeof spec === 'number' && toOneDecimalPlace ? spec.toFixed(1) : spec;
  return `${(suffix ? specValue + suffix : specValue) || ''}`;
};

export const updateSpecsWithNotAvailable = (
  specs: SpecValuesByTitle[],
  indices: boolean[]
) => {
  return specs.map((spec: SpecValuesByTitle) => {
    let trackingIndex = 0;

    return {
      ...spec,
      values: indices.map((valid: boolean) => {
        if (valid) {
          return spec.values[trackingIndex++];
        }

        return FeatureStatus.NOT_AVAILABLE;
      }),
    };
  });
};

/**
 * In stock-car columns, swap the fallback for `FeatureStatus.NOT_AVAILABLE` on
 * rows stock data never provides so they read "Not available".
 */
export const markNeverOnStockSpecs = <
  T extends { label: string; values: string[] },
>(
  specs: T[],
  isStockFlags: boolean[],
  fallback: string,
  neverOnStockLabels: string[]
): T[] =>
  specs.map((spec) =>
    neverOnStockLabels.includes(spec.label)
      ? {
          ...spec,
          values: spec.values.map((value, index) =>
            isStockFlags[index] && value === fallback
              ? FeatureStatus.NOT_AVAILABLE
              : value
          ),
        }
      : spec
  );

/**
 * `fallback` fills cells a configuration has no value for. Pass an array to
 * use a different fallback per configuration column.
 */
export const mergeSpecs = (
  specs: Array<object>,
  fallback: string | string[]
) => {
  // Get merged set of specs and remove duplicates
  let flattenSpecs = removeDuplicatesByKey(specs.flat(), 'label');
  let mergedSpecs: any[] = [];

  // Loop through specs and combine values from each configuration
  flattenSpecs.forEach((spec: any, index: number) => {
    mergedSpecs[index] = {
      label: spec.label,
      values: [],
    };

    specs.forEach((specsPerConfiguration: any, configurationIndex: number) => {
      const matchingSpec = specsPerConfiguration.find(
        (configSpec: any) => configSpec.label === spec.label
      );

      mergedSpecs[index].values[configurationIndex] = matchingSpec
        ? matchingSpec.text
        : Array.isArray(fallback)
          ? fallback[configurationIndex]
          : fallback;
    });
  });
  return mergedSpecs;
};

export const configurationsContainsFuelType = (
  fuelType: fuelType,
  configurations: GlobalStoreStateConfiguration[]
) =>
  !!configurations.find(
    ({ pdbData }) =>
      normalizeFuelTypeName(
        pdbData?.technicalSpecifications?.engine.fuelTypeName
      ) === fuelType
  );

export const getModelTrim = (
  model?: string,
  trim?: string,
  fallbackMessage?: string
) =>
  (model && trim ? [model, trim].filter(Boolean).join(' ') : fallbackMessage) ??
  '';

export const technologyItemState = (
  item: TechnologyItem,
  configuration: GlobalStoreStateConfiguration
): FeatureStatus => {
  if (isNotBuildable(configuration.pdbData)) {
    return FeatureStatus.NOT_AVAILABLE;
  }

  const standard = configuration?.productData?.technologyItems?.standard ?? [];
  const extras = configuration?.productData?.technologyItems?.extras ?? [];

  if (!!standard.find((feature: TechnologyItem) => feature.id === item.id)) {
    return FeatureStatus.STANDARD_SPEC;
  } else if (
    !!extras.find((feature: TechnologyItem) => feature.id === item.id)
  ) {
    return FeatureStatus.OPTIONAL_EXTRA;
  } else {
    return FeatureStatus.NOT_INCLUDED;
  }
};

export const getDisabledModelMessage = (
  contentManager: ContentManager,
  modelTitle: string,
  derivativeTitle: string,
) => {
  return contentManager.getParsedString('vehicle-not-available', [
    {
      identifier: 'model',
      value: modelTitle,
    },
    {
      identifier: 'derivative',
      value: derivativeTitle,
    },
  ]);
};

export const getSelectedVideo = (
  technologyItems: TechnologyItem[],
  url: string
) => {
  return technologyItems?.find(
    (item: TechnologyItem) => item.assetCollection?.[0]?.url === url
  );
};

export const getBespokeSpecItemsCodes = (bespokeSpecItems: BespokeSpecItem[]) =>
  bespokeSpecItems.map(
    ({ optionId }) => optionId
  );

export const getFinanceCtaUnavailableUrl = (
  baseUrl: string,
  configuration: GlobalStoreStateConfiguration
) => `${baseUrl}/v/${
        configuration.marketingShortCode
          ? `C/${configuration.marketingShortCode}`
          : configuration.vin ? `V/${configuration.vin}`
          : `S/${configuration.orderNumber}`
      }${
        !configuration.marketingShortCode && configuration.bespokeSpecItems?.length 
          ? `/o/${getBespokeSpecItemsCodes(configuration.bespokeSpecItems)}`
          : ''
      }/vwuk-fc!`;
